import { useEffect, useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import Topbar from "../components/Topbar";
import ChartSection from "./analyticsDashboard/components/ChartSection";
import DataUnavailableCard, { TECHNICAL_ERROR_MESSAGE } from "./analyticsDashboard/components/DataUnavailableCard";
import DemoAnalyticsBanner from "./analyticsDashboard/components/DemoAnalyticsBanner";
import FiltersSection from "./analyticsDashboard/components/FiltersSection";
import FiltersLoadingPlaceholder from "./analyticsDashboard/components/FiltersLoadingPlaceholder";
import GuestCountDisplay from "./analyticsDashboard/components/GuestCountDisplay";
import KpiSection from "./analyticsDashboard/components/KpiSection";
import LoadingOverlay from "./analyticsDashboard/components/LoadingOverlay";
import ObjectSelectCard from "./analyticsDashboard/components/ObjectSelectCard";
import PriceDetailsModal from "./analyticsDashboard/components/PriceDetailsModal";
import TableCard from "./analyticsDashboard/components/TableCard";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import { CHART_PALETTE, DEFAULT_FILTERS, DEFAULT_FILTER_OPTIONS, DEFAULT_KPI } from "./analyticsDashboard/constants";
import type {
  AppliedFilterItem,
  ChartData,
  FilterOption,
  FilterOptions,
  GridRow,
  KpiItem,
  PriceDetailsItem,
} from "./analyticsDashboard/types";
import { toRows } from "./analyticsDashboard/utils";
import { downloadPricePositionXlsx, getFilters, getMetrics, getPriceOnDate } from "./analyticsDashboard/api";
import {
  DEMO_ANALYTICS_GROUP_ID,
  DEMO_ANALYTICS_OBJECT_OPTION,
  DEMO_ANALYTICS_OBJECT_VALUE,
} from "./analyticsDashboard/demoObject";
import {
  LAST_PARSING_DATE_LABEL,
  isLastParsingDateInfoReady,
  useLastParsingDateInfo,
} from "./analyticsDashboard/hooks/useLastParsingDate";
import {
  isAvailableParsingDatesInfoReady,
  normalizeAvailableDate,
  useAvailableParsingDates,
} from "./analyticsDashboard/hooks/useAvailableParsingDates";
import { getSubscriptionState } from "../utils/subscription";
import {
  buildAnalyticsGuestsPayload,
  buildGuestSelectionOptions,
} from "../utils/guests";
import { loadHotelsDisplaySettings } from "../utils/hotelDisplaySettings";

const formatIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const getTodayDate = () => formatIsoDate(new Date());
const DAY_TYPE_LABELS: Record<string, string> = {
  dayoff: "Только выходные",
  workday: "Только будние дни",
};
const formatDaysStayLabel = (value: number) => {
  if (value === 1) return "1 ночь";
  if (value < 5) return `${value} ночи`;
  return `${value} ночей`;
};

export default function AnalyticsDashboardPage() {
  const { user, company_id: companyId } = useAuth();
  const subscriptionState = getSubscriptionState(user?.subscription_expires_on);
  const isSubscriptionExpired = subscriptionState.isExpired;

  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, date: getTodayDate() });
  const [daysStay, setDaysStay] = useState<number>(1);
  const [guests, setGuests] = useState<string>("2");
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [kpi, setKpi] = useState<KpiItem[]>(DEFAULT_KPI);
  const [averageRows, setAverageRows] = useState<GridRow[]>([]);
  const [minRows, setMinRows] = useState<GridRow[]>([]);
  const [chartData, setChartData] = useState<ChartData>({ dates: [], series: [] });
  const [visibleCount, setVisibleCount] = useState(10);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(DEFAULT_FILTER_OPTIONS);
  const [checkInRange, setCheckInRange] = useState<{ from: string; to: string } | null>(null);
  const [hotelOptions, setHotelOptions] = useState<FilterOption[]>([]);
  const [hotelGroupMap, setHotelGroupMap] = useState<Record<string, number | null>>({});
  const [hotelMinStayMap, setHotelMinStayMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsMap, setHotelGuestsMap] = useState<Record<string, number[] | null>>({});
  const [hotelGuestsChildMap, setHotelGuestsChildMap] = useState<Record<string, number[] | null>>({});
  const [baseHotelFilterNames, setBaseHotelFilterNames] = useState<string[]>([]);
  const [competitorHotelNameMap, setCompetitorHotelNameMap] = useState<Record<string, string>>({});
  const [selectedHotelHasCompetitors, setSelectedHotelHasCompetitors] = useState<boolean | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsData, setDetailsData] = useState<PriceDetailsItem[]>([]);
  const [detailsTitle, setDetailsTitle] = useState("Детальная таблица OTA");
  const [detailsSubtitle, setDetailsSubtitle] = useState<string | undefined>(undefined);
  const [detailsAppliedFilters, setDetailsAppliedFilters] = useState<AppliedFilterItem[]>([]);
  const priceRequestIdRef = useRef(0);
  const lastDefaultDateRef = useRef<string | null>(filters.date);

  const selectedGroupId = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGroupMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGroupMap]);
  const selectedHotelMinStay = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelMinStayMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelMinStayMap]);
  const selectedHotelGuests = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsMap]);
  const selectedHotelGuestsChild = useMemo(() => {
    if (!filters.hotel) return null;
    return hotelGuestsChildMap[filters.hotel] ?? null;
  }, [filters.hotel, hotelGuestsChildMap]);
  const selectedHotelMinStayOptions = useMemo(
    () => (selectedHotelMinStay?.length ? selectedHotelMinStay : [1]),
    [selectedHotelMinStay],
  );
  const selectedHotelGuestOptions = useMemo(
    () => buildGuestSelectionOptions(selectedHotelGuests, selectedHotelGuestsChild),
    [selectedHotelGuests, selectedHotelGuestsChild],
  );
  const hasSelectedHotelGuests = Boolean(filters.hotel);
  const hasHotelSelected = Boolean(filters.hotel);
  const isDemoAnalytics = filters.hotel === DEMO_ANALYTICS_OBJECT_VALUE;
  const shouldBlockAnalyticsRequests = hasHotelSelected && !isDemoAnalytics && selectedHotelHasCompetitors !== true;
  const lastParsingDate = useLastParsingDateInfo(selectedGroupId, daysStay, !isSubscriptionExpired);
  const isLastParsingDateReady = isLastParsingDateInfoReady(
    lastParsingDate,
    selectedGroupId,
    daysStay,
    !isSubscriptionExpired,
  );
  const availableParsingDates = useAvailableParsingDates(selectedGroupId, daysStay, guests, !isSubscriptionExpired);
  const isAvailableParsingDatesReady = isAvailableParsingDatesInfoReady(
    availableParsingDates,
    selectedGroupId,
    daysStay,
    guests,
    !isSubscriptionExpired,
  );
  const defaultDataCollectionDate = lastParsingDate.date ?? getTodayDate();
  const selectedParsingDate = filters.date || defaultDataCollectionDate;
  const shouldBlockParsingDateRequests = Boolean(selectedGroupId) && (
    !isAvailableParsingDatesReady ||
    Boolean(availableParsingDates.error) ||
    availableParsingDates.dates.length === 0 ||
    !availableParsingDates.dateSet.has(selectedParsingDate)
  );
  const filtersLoading = hasHotelSelected && (optionsLoading || availableParsingDates.loading);
  const shouldWaitForDefaultDate =
    isLastParsingDateReady &&
    Boolean(lastParsingDate.date) &&
    filters.date !== lastParsingDate.date &&
    (!filters.date || filters.date === lastDefaultDateRef.current);

  const formatDateFull = (value: string) => {
    const [year, month, day] = value.split("-");
    if (!year || !month || !day) return value;
    return `${day}.${month}.${year}`;
  };
  const buildHotelFilterPayload = (currentFilters: typeof filters) => {
    if (!currentFilters.competitor.length) return undefined;
    return Array.from(
      new Set([
        ...baseHotelFilterNames,
        ...currentFilters.competitor.map((value) => competitorHotelNameMap[value] ?? value),
      ]),
    );
  };
  const getOptionLabels = (options: FilterOption[], values: string[]) =>
    values.map((value) => options.find((option) => option.value === value)?.label ?? value);
  const buildBaseFiltersPayload = (currentFilters: typeof filters) => ({
    category: currentFilters.category.length ? currentFilters.category : undefined,
    ota: currentFilters.ota.length ? currentFilters.ota : undefined,
    board_type: currentFilters.meals.length ? currentFilters.meals : undefined,
    refund_policy: currentFilters.refund.length ? currentFilters.refund : undefined,
    payment_type: currentFilters.payment.length ? currentFilters.payment : undefined,
  });
  const buildDaysStayPayload = () => (daysStay > 1 ? { days_stay: daysStay } : {});
  const buildGuestsPayload = () => buildAnalyticsGuestsPayload(guests);
  const buildDateRangePayload = (currentFilters: typeof filters) => ({
    date_from: currentFilters.dateIntervalFrom || undefined,
    date_to: currentFilters.dateIntervalTo || undefined,
  });
  const buildDayTypePayload = (currentFilters: typeof filters) => ({
    day_type: currentFilters.dayType || undefined,
  });
  const buildAppliedFiltersHeader = (currentFilters: typeof filters): AppliedFilterItem[] => {
    const items: AppliedFilterItem[] = [];
    const selectedHotel = hotelOptions.find((option) => option.value === currentFilters.hotel);
    if (selectedHotel) items.push({ label: "Объект", value: selectedHotel.label });
    if (currentFilters.dataSource.length) {
      items.push({ label: "Сбор данных", value: getOptionLabels(filterOptions.dataSource, currentFilters.dataSource).join(", ") });
    }
    if (currentFilters.category.length) {
      items.push({ label: "Категория", value: getOptionLabels(filterOptions.category, currentFilters.category).join(", ") });
    }
    if (currentFilters.ota.length) {
      items.push({ label: "OTA", value: getOptionLabels(filterOptions.ota, currentFilters.ota).join(", ") });
    }
    if (currentFilters.meals.length) {
      items.push({ label: "Питание", value: getOptionLabels(filterOptions.meals, currentFilters.meals).join(", ") });
    }
    if (currentFilters.refund.length) {
      items.push({ label: "Возврат", value: getOptionLabels(filterOptions.refund, currentFilters.refund).join(", ") });
    }
    if (currentFilters.payment.length) {
      items.push({ label: "Оплата", value: getOptionLabels(filterOptions.payment, currentFilters.payment).join(", ") });
    }
    if (currentFilters.competitor.length) {
      items.push({ label: "Конкурент", value: getOptionLabels(filterOptions.competitors, currentFilters.competitor).join(", ") });
    }
    if (currentFilters.room.length) {
      items.push({ label: "Комната", value: getOptionLabels(filterOptions.room, currentFilters.room).join(", ") });
    }
    if (currentFilters.date) {
      items.push({ label: "Дата сбора", value: formatDateFull(currentFilters.date) });
    }
    if (currentFilters.dateIntervalFrom || currentFilters.dateIntervalTo) {
      items.push({
        label: "Дата заселения",
        value: `${currentFilters.dateIntervalFrom ? formatDateFull(currentFilters.dateIntervalFrom) : "—"} - ${
          currentFilters.dateIntervalTo ? formatDateFull(currentFilters.dateIntervalTo) : "—"
        }`,
      });
    }
    if (currentFilters.dayType) {
      items.push({ label: "Дни недели", value: DAY_TYPE_LABELS[currentFilters.dayType] ?? currentFilters.dayType });
    }
    return items;
  };

  useEffect(() => {
    if (!companyId) return;
    let isMounted = true;
    setOptionsLoading(true);

    listHotels(companyId, { page: 1, per_page: 200 })
      .then(async (hotelsResponse) => {
        if (!isMounted) return;
        const hotels = hotelsResponse.items.map((item) => ({ label: item.name, value: String(item.id) }));
        if (hotelsResponse.items.length === 0) {
          const nextFilters = { ...DEFAULT_FILTERS, hotel: DEMO_ANALYTICS_OBJECT_VALUE, date: getTodayDate() };
          lastDefaultDateRef.current = nextFilters.date;
          setHotelGroupMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: DEMO_ANALYTICS_GROUP_ID });
          setHotelMinStayMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelGuestsChildMap({ [DEMO_ANALYTICS_OBJECT_VALUE]: null });
          setHotelOptions([DEMO_ANALYTICS_OBJECT_OPTION]);
          setBaseHotelFilterNames([]);
          setCompetitorHotelNameMap({});
          setNoData(false);
          setError(null);
          setFilters(nextFilters);
          return;
        }
        const displaySettingsMap = await loadHotelsDisplaySettings(hotelsResponse.items);
        if (!isMounted) return;
        const groupMap: Record<string, number | null> = {};
        const minStayMap: Record<string, number[] | null> = {};
        const guestsMap: Record<string, number[] | null> = {};
        const guestsChildMap: Record<string, number[] | null> = {};
        hotelsResponse.items.forEach((item) => {
          const displaySettings = displaySettingsMap[String(item.id)];
          groupMap[String(item.id)] = item.group_id ?? null;
          minStayMap[String(item.id)] = displaySettings?.min_stay ?? item.min_stay ?? null;
          guestsMap[String(item.id)] = displaySettings?.guests ?? item.guests ?? null;
          guestsChildMap[String(item.id)] = displaySettings?.guests_child ?? item.guests_child ?? null;
        });
        setHotelGroupMap(groupMap);
        setHotelMinStayMap(minStayMap);
        setHotelGuestsMap(guestsMap);
        setHotelGuestsChildMap(guestsChildMap);
        setHotelOptions(hotels);
        setFilters((prev) => {
          if (hotelsResponse.items.length === 1) {
            const nextFilters = { ...DEFAULT_FILTERS, hotel: String(hotelsResponse.items[0].id), date: getTodayDate() };
            lastDefaultDateRef.current = nextFilters.date;
            return nextFilters;
          }
          if (hotels.some((item) => item.value === prev.hotel)) {
            return prev;
          }
          const nextFilters = { ...DEFAULT_FILTERS, date: getTodayDate() };
          lastDefaultDateRef.current = nextFilters.date;
          return nextFilters;
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Не удалось загрузить фильтры");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    let isMounted = true;
    setOptionsLoading(true);
    setSelectedHotelHasCompetitors(isDemoAnalytics ? true : null);

    getFilters(selectedGroupId)
      .then((filtersResponse) => {
        if (!isMounted) return;
        const mapOptions = (
          items?: Array<{ key: string; value: string } | string> | Record<string, string>,
        ) => {
          if (!items) return [];
          if (!Array.isArray(items)) {
            return Object.entries(items).map(([key, value]) => ({ label: value, value: key }));
          }
          return items
            .map((item) => {
              if (typeof item === "string") return { label: item, value: item };
              if (!item) return null;
              const value = item.key ?? item.value;
              const label = item.value ?? item.key;
              if (!value || !label) return null;
              return { label, value };
            })
            .filter((item): item is { label: string; value: string } => Boolean(item));
        };
        const mapCompetitorOptions = (
          items?: Array<{ hotel_id: string | number; hotel_name: string; is_base: boolean | 0 | 1 | "0" | "1" } | { key: string; value: string } | string> | Record<string, string>,
        ) => {
          if (!items) {
            setBaseHotelFilterNames([]);
            setCompetitorHotelNameMap({});
            return [];
          }
          if (!Array.isArray(items)) {
            setBaseHotelFilterNames([]);
            setCompetitorHotelNameMap({});
            return mapOptions(items);
          }

          const baseNames: string[] = [];
          const nextCompetitorNameMap: Record<string, string> = {};
          const competitors = items
            .map((item) => {
              if (typeof item === "string") return { label: item, value: item };
              if (!item) return null;
              if ("hotel_id" in item && "hotel_name" in item) {
                const isBase = item.is_base === true || item.is_base === 1 || item.is_base === "1";
                if (isBase) {
                  baseNames.push(item.hotel_name);
                  return null;
                }
                nextCompetitorNameMap[String(item.hotel_id)] = item.hotel_name;
                return { label: item.hotel_name, value: String(item.hotel_id) };
              }
              const value = item.key ?? item.value;
              const label = item.value ?? item.key;
              if (!value || !label) return null;
              return { label, value };
            })
            .filter((item): item is { label: string; value: string } => Boolean(item));

          setBaseHotelFilterNames(baseNames);
          setCompetitorHotelNameMap(nextCompetitorNameMap);
          return competitors;
        };

        const competitorOptions = mapCompetitorOptions(filtersResponse.hotel);
        setSelectedHotelHasCompetitors(isDemoAnalytics || competitorOptions.length > 0);
        setFilterOptions({
          dataSource: mapOptions(filtersResponse.data_source),
          room: mapOptions(filtersResponse.room),
          category: mapOptions(filtersResponse.category),
          ota: mapOptions(filtersResponse.ota),
          meals: mapOptions(filtersResponse.board_type),
          refund: mapOptions(filtersResponse.refund_policy),
          payment: mapOptions(filtersResponse.payment_type),
          competitors: competitorOptions,
        });
        setCheckInRange(filtersResponse.check_in_dates ?? null);
        setFilters((prev) => ({ ...prev, date: prev.date || getTodayDate() }));
      })
      .catch(() => {
        if (!isMounted) return;
        setSelectedHotelHasCompetitors(null);
        setError("Не удалось загрузить фильтры");
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isDemoAnalytics, selectedGroupId]);

  useEffect(() => {
    if (!selectedHotelMinStay?.length) {
      setDaysStay(1);
      return;
    }
    if (!selectedHotelMinStay.includes(daysStay)) {
      setDaysStay(selectedHotelMinStay[0]);
    }
  }, [daysStay, selectedHotelMinStay]);

  useEffect(() => {
    if (!selectedHotelGuestOptions.some((option) => option.value === guests)) {
      setGuests(selectedHotelGuestOptions[0]?.value ?? "2");
    }
  }, [guests, selectedHotelGuestOptions]);

  useEffect(() => {
    const lastDate = lastParsingDate.date;
    if (!lastDate) return;
    const currentDefaultDate = lastDefaultDateRef.current;
    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const shouldApplyDefault = !prev.date || prev.date === currentDefaultDate;
      if (!shouldApplyDefault || prev.date === lastDate) return prev;
      return { ...prev, date: lastDate };
    });
    lastDefaultDateRef.current = lastDate;
  }, [lastParsingDate.date]);

  useEffect(() => {
    if (!selectedGroupId || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    setFilters((prev) => {
      if (!prev.hotel) return prev;
      const nextDate = normalizeAvailableDate(prev.date, defaultDataCollectionDate, availableParsingDates.dates);
      if (prev.date === nextDate) return prev;
      return { ...prev, date: nextDate };
    });
  }, [
    availableParsingDates.dates,
    availableParsingDates.datesKey,
    availableParsingDates.error,
    defaultDataCollectionDate,
    isAvailableParsingDatesReady,
    selectedGroupId,
  ]);

  useEffect(() => {
    if (!hasHotelSelected) {
      setNoData(false);
      setSelectedHotelHasCompetitors(null);
    }
  }, [hasHotelSelected]);

  useEffect(() => {
    if (!hasHotelSelected || shouldBlockAnalyticsRequests) {
      setKpi(DEFAULT_KPI);
      setAverageRows([]);
      setMinRows([]);
      setChartData({ dates: [], series: [] });
    }
  }, [hasHotelSelected, shouldBlockAnalyticsRequests]);

  const fetchPriceMetrics = async (nextFilters?: typeof filters) => {
    if (isSubscriptionExpired) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setKpi(DEFAULT_KPI);
      setAverageRows([]);
      setMinRows([]);
      setChartData({ dates: [], series: [] });
      return;
    }
    const currentFilters = nextFilters ?? filters;
    const requestDate = currentFilters.date || defaultDataCollectionDate;
    if (shouldBlockAnalyticsRequests || !availableParsingDates.dateSet.has(requestDate)) {
      setLoading(false);
      setNoData(false);
      setError(null);
      setKpi(DEFAULT_KPI);
      setAverageRows([]);
      setMinRows([]);
      setChartData({ dates: [], series: [] });
      return;
    }
    const requestId = ++priceRequestIdRef.current;
    setLoading(true);
    setNoData(false);
    setError(null);
    setChartData({ dates: [], series: [] });

    try {
      if (!selectedGroupId) return;
      const payload = {
        group_id: selectedGroupId,
        ...buildBaseFiltersPayload(currentFilters),
        ...buildDaysStayPayload(),
        ...buildGuestsPayload(),
        ...buildDateRangePayload(currentFilters),
        ...buildDayTypePayload(currentFilters),
        hotel: buildHotelFilterPayload(currentFilters),
        date: requestDate,
      };

      const response = await getMetrics(payload);
      if (requestId !== priceRequestIdRef.current) return;
      const items = response.items ?? [];
      setNoData(items.length === 0);
      const summary = response.summary ?? null;
      const avgMapped = items.map((item) => ({
        date: item.date_from,
        client: item.client_avg,
        competitor: item.competitors_avg,
        diffPercent: item.difference,
      }));
      const minMapped = items.map((item) => ({
        date: item.date_from,
        client: item.client_min ?? null,
        competitor: item.competitors_min ?? null,
        diffPercent: item.difference_min ?? null,
        clientMinData: item.client_min_data ?? null,
        competitorMinData: item.competitor_min_data ?? null,
      }));
      const hasMinData = items.some(
        (item) => item.client_min !== undefined || item.competitors_min !== undefined || item.difference_min !== undefined,
      );
      const hasMaxData = items.some(
        (item) => item.client_max !== undefined || item.competitors_max !== undefined || item.difference_max !== undefined,
      );

      setAverageRows(toRows(avgMapped, 0));
      setMinRows(toRows(hasMinData ? minMapped : [], 100));
      setChartData({
        dates: items.map((item) => item.date_from.slice(5).split("-").reverse().join(".")),
        series: [
          ...(hasMinData
            ? [
                {
                  name: "Клиент (минимальное)",
                  data: items.map((item) => item.client_min ?? null),
                  style: "dashed" as const,
                },
              ]
            : []),
          { name: "Клиент (среднее)", data: items.map((item) => item.client_avg) },
          ...(hasMaxData
            ? [
                {
                  name: "Клиент (максимальное)",
                  data: items.map((item) => item.client_max ?? null),
                },
              ]
            : []),
          ...(hasMinData
            ? [
                {
                  name: "Конкуренты (минимальное)",
                  data: items.map((item) => item.competitors_min ?? null),
                  style: "dashed" as const,
                },
              ]
            : []),
          { name: "Конкуренты (среднее)", data: items.map((item) => item.competitors_avg) },
          ...(hasMaxData
            ? [
                {
                  name: "Конкуренты (максимальное)",
                  data: items.map((item) => item.competitors_max ?? null),
                },
              ]
            : []),
        ],
      });

      if (summary) {
        const formatCurrency = (value?: number | null) =>
          typeof value === "number" ? value.toLocaleString("ru-RU") : "—";
        const formatDelta = (value?: number | null) =>
          typeof value === "number" ? `${value > 0 ? "+" : ""}${value.toLocaleString("ru-RU")}` : "—";
        const formatPercent = (value?: number | null) =>
          typeof value === "number" ? `(${value > 0 ? "+" : ""}${value.toFixed(1)}%)` : undefined;
        const resolveTone = (value?: number | null) => {
          if (typeof value !== "number") return undefined;
          if (value > 0) return "text-emerald-600";
          if (value < 0) return "text-rose-600";
          return "text-slate-500";
        };
        const deltaTone = resolveTone(summary.delta_avg_percent) ?? resolveTone(summary.delta_avg_value);

        setKpi([
          {
            label: "Средняя дельта",
            value: formatDelta(summary.delta_avg_value),
            suffix: formatPercent(summary.delta_avg_percent),
            tone: deltaTone,
            description: "к средним ценам конкурентов",
            accent: "emerald",
          },
          {
            label: "Средняя цена клиента",
            value: formatCurrency(summary.client_avg_value),
            description: "по выбранному периоду",
            accent: "indigo",
          },
          {
            label: "Средняя цена конкурентов",
            value: formatCurrency(summary.competitors_avg_value),
            description: "по выбранному периоду",
            accent: "violet",
          },
          {
            label: "Дней когда конкурент выше/ниже",
            badges: [
              { label: `Выше: ${summary.days_competitor_higher ?? "—"}`, className: "bg-emerald-50 text-emerald-700" },
              { label: `Ниже: ${summary.days_competitor_lower ?? "—"}`, className: "bg-rose-50 text-rose-700" },
            ],
            description: "дней с отклонением",
            accent: "amber",
          },
        ]);
      } else {
        setKpi(DEFAULT_KPI);
      }
    } catch {
      if (requestId !== priceRequestIdRef.current) return;
      setNoData(false);
      setError("Не удалось загрузить данные дашборда");
    } finally {
      if (requestId === priceRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (
      isSubscriptionExpired ||
      !hasHotelSelected ||
      !selectedGroupId ||
      shouldBlockAnalyticsRequests ||
      shouldBlockParsingDateRequests ||
      !isLastParsingDateReady ||
      !isAvailableParsingDatesReady ||
      shouldWaitForDefaultDate
    ) return;
    fetchPriceMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupId, daysStay, guests, isSubscriptionExpired, shouldBlockAnalyticsRequests, shouldBlockParsingDateRequests, isLastParsingDateReady, isAvailableParsingDatesReady, shouldWaitForDefaultDate]);

  const handleApply = async () => {
    if (loading || isSubscriptionExpired || shouldBlockAnalyticsRequests || shouldBlockParsingDateRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady) return;
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    await fetchPriceMetrics();
  };

  const handleReset = async () => {
    if (loading || isSubscriptionExpired || shouldBlockAnalyticsRequests || !isLastParsingDateReady || !isAvailableParsingDatesReady || availableParsingDates.error) return;
    const resetDate = normalizeAvailableDate(defaultDataCollectionDate, defaultDataCollectionDate, availableParsingDates.dates);
    const resetFilters = {
      ...DEFAULT_FILTERS,
      hotel: filters.hotel,
      date: resetDate,
    };
    lastDefaultDateRef.current = resetFilters.date;
    setFilters(resetFilters);
    setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
    await fetchPriceMetrics(resetFilters);
  };

  const handleOpenDetails = async (row: GridRow, isAvg: boolean) => {
    if (!selectedGroupId || isSubscriptionExpired || shouldBlockAnalyticsRequests || shouldBlockParsingDateRequests) return;
    const currentFilters = filters;
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsData([]);
    setDetailsTitle("Детальная таблица OTA");
    setDetailsSubtitle(`Дата: ${formatDateFull(row.date)}`);
    setDetailsAppliedFilters(buildAppliedFiltersHeader(currentFilters));

    try {
      const response = await getPriceOnDate({
        group_id: selectedGroupId,
        date: row.date,
        request_date: currentFilters.date || defaultDataCollectionDate,
        ...buildBaseFiltersPayload(currentFilters),
        ...buildDaysStayPayload(),
        ...buildGuestsPayload(),
        data_source: currentFilters.dataSource.length ? currentFilters.dataSource : undefined,
        room: currentFilters.room.length ? currentFilters.room : undefined,
        hotel: buildHotelFilterPayload(currentFilters),
        is_avg: isAvg,
      });
      const mapped = response.map((item) => ({
        hotelId: String(item.hotel_id),
        hotelName: item.hotel_name,
        roomKey: `${String(item.hotel_id)}:${item.category ?? ""}:${item.room_name}`,
        roomName: item.room_name,
        isBase: item.is_base === true || item.is_base === 1 || item.is_base === "1",
        category: item.category ?? "",
        prices: item.prices ?? {},
      }));
      setDetailsData(mapped);
    } catch {
      setDetailsError("Не удалось загрузить детализацию цен");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    if (!selectedGroupId || downloadLoading || isSubscriptionExpired || shouldBlockAnalyticsRequests) return;
    if (shouldBlockParsingDateRequests) return;

    setDownloadLoading(true);

    try {
      const payload = {
        group_id: selectedGroupId,
        ...buildBaseFiltersPayload(filters),
        ...buildDaysStayPayload(),
        ...buildGuestsPayload(),
        ...buildDateRangePayload(filters),
        ...buildDayTypePayload(filters),
        hotel: buildHotelFilterPayload(filters),
        date: filters.date || defaultDataCollectionDate,
      };
      const { blob, filename } = await downloadPricePositionXlsx(payload);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename || `price-position-${filters.hotel || "report"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (downloadError) {
      console.error("Не удалось скачать отчет дашборда", downloadError);
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="px-6 py-8">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-6">
          <section className="relative overflow-hidden rounded-3xl border border-white/70 bg-white/90 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="pointer-events-none absolute inset-y-0 right-0 w-40 translate-x-10 rounded-full bg-gradient-to-b from-indigo-100 to-purple-100 blur-3xl opacity-60" />
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="mt-2 text-3xl font-bold text-slate-900">Дашборд</h1>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                <div className="flex flex-col gap-3 self-start">
                  {hasHotelSelected && (
                    <div className="flex items-center gap-3 self-start sm:self-auto">
                    <span className="text-xs font-semibold uppercase tracking-[0.08em] text-indigo-500">Срок заселения</span>
                    <div className="inline-flex rounded-full border border-indigo-200 bg-white p-1 shadow-sm">
                      {selectedHotelMinStayOptions.map((value) => {
                        const isActive = daysStay === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => {
                              setDaysStay(value);
                              setAppliedAt(new Date().toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" }));
                            }}
                            className={[
                              "rounded-full px-3 py-1 text-xs font-semibold transition",
                              isActive ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-indigo-50",
                            ].join(" ")}
                            aria-pressed={isActive}
                          >
                            {formatDaysStayLabel(value)}
                          </button>
                        );
                      })}
                    </div>
                    </div>
                  )}
                  {hasHotelSelected && hasSelectedHotelGuests && (
                    <GuestCountDisplay value={guests} options={selectedHotelGuestOptions} onChange={setGuests} />
                  )}
                </div>
                <div className="rounded-2xl border border-indigo-100 bg-indigo-50/70 px-5 py-4 text-sm text-indigo-700">
                  {lastParsingDate.display ? `${LAST_PARSING_DATE_LABEL}: ${lastParsingDate.display}` : appliedAt
                    ? `Фильтры обновлены в ${appliedAt}`
                    : "Выберите объект, задайте фильтры и нажмите «Применить»"}
                </div>
              </div>
            </div>
          </section>

          {error && <DataUnavailableCard message={TECHNICAL_ERROR_MESSAGE} variant="error" />}

          {isDemoAnalytics && <DemoAnalyticsBanner />}

          <ObjectSelectCard
            value={filters.hotel}
            options={hotelOptions}
            onChange={(value) => {
              setNoData(false);
              setSelectedHotelHasCompetitors(value === DEMO_ANALYTICS_OBJECT_VALUE ? true : null);
              const nextFilters = { ...DEFAULT_FILTERS, hotel: value, date: getTodayDate() };
              lastDefaultDateRef.current = nextFilters.date;
              setFilters(nextFilters);
            }}
            loading={optionsLoading}
          />

          {hasHotelSelected && filtersLoading ? (
            <FiltersLoadingPlaceholder label="Загружаем фильтры и доступные даты..." />
          ) : hasHotelSelected && (
            <FiltersSection
              filters={filters}
              options={filterOptions}
              onChange={setFilters}
              onApply={handleApply}
              onReset={handleReset}
              checkInRange={checkInRange}
              showCompetitors
              showDateInterval
              showDayType
              dateLabel="Дата сбора данных"
              dateIntervalLabel="Дата заселения"
              loading={loading || optionsLoading || !isLastParsingDateReady || shouldBlockAnalyticsRequests}
              applyDisabled={shouldBlockParsingDateRequests}
              parsingDateAvailability={{
                dates: availableParsingDates.dates,
                loading: availableParsingDates.loading,
                error: availableParsingDates.error,
                restrictDate: true,
              }}
            />
          )}

          {!hasHotelSelected && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center text-slate-500">
              Выберите объект размещения, чтобы загрузить метрики.
            </div>
          )}

          {hasHotelSelected && !error && optionsLoading && (
            <section className="relative min-h-[160px] overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <LoadingOverlay visible label="Загружаем данные..." />
            </section>
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !optionsLoading && loading && (selectedHotelHasCompetitors !== true || noData) && (
            <section className="relative min-h-[220px] overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
              <LoadingOverlay visible label="Загружаем данные..." />
            </section>
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === false && (
            <DataUnavailableCard
              message={
                <>
                  Для начала работы добавьте конкурентов в{" "}
                  <Link className="text-indigo-600 underline decoration-indigo-300 underline-offset-4 hover:text-indigo-700" to={`/my/objects/${filters.hotel}`}>
                    объект размещения
                  </Link>
                </>
              }
            />
          )}

          {hasHotelSelected && !isSubscriptionExpired && !error && !loading && selectedHotelHasCompetitors === true && noData && <DataUnavailableCard />}

          {hasHotelSelected && !isSubscriptionExpired && !error && selectedHotelHasCompetitors === true && !noData && (
            <section className="relative">
              <LoadingOverlay visible={loading} label="Загружаем метрики..." />
              <div className={loading ? "pointer-events-none opacity-60" : ""}>
                <div className="mb-4 flex justify-end">
                  <button
                    type="button"
                    onClick={handleDownloadXlsx}
                    disabled={downloadLoading}
                    className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Download size={14} />
                    {downloadLoading ? "Скачиваем..." : "Скачать XLSX"}
                  </button>
                </div>
                <KpiSection items={kpi} />

                <section className="mt-6 grid gap-6 lg:grid-cols-2">
                  <TableCard
                    title="Средняя цена конкурентов"
                    rows={averageRows}
                    accent="violet"
                    visibleCount={visibleCount}
                    onPriceClick={(row: GridRow) => handleOpenDetails(row, true)}
                  />
                  <TableCard
                    title="Минимальная цена конкурентов"
                    rows={minRows}
                    accent="emerald"
                    visibleCount={visibleCount}
                    onPriceClick={(row: GridRow) => handleOpenDetails(row, false)}
                    showMinPriceInfo
                  />
                </section>

                <div className="mt-3 flex flex-wrap items-center justify-end gap-2 text-xs text-slate-500">
                  <span>Отображать записей:</span>
                  <select
                    className="cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 outline-none"
                    value={visibleCount}
                    onChange={(event) => setVisibleCount(Number(event.target.value))}
                  >
                    {[5, 10, 25, 50, 100, 150].map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-6">
                  <ChartSection data={chartData} palette={CHART_PALETTE} />
                </div>
              </div>
            </section>
          )}
        </div>
      </main>

      <PriceDetailsModal
        open={detailsOpen}
        title={detailsTitle}
        subtitle={detailsSubtitle}
        appliedFilters={detailsAppliedFilters}
        data={detailsData}
        loading={detailsLoading}
        error={detailsError}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
}
